import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  CustomModalConfig,
  NgbCustomModalComponent,
} from 'components/modal-windows/ngb-custom-modal/ngb-custom-modal.component';
import { CustomFields } from 'features/custom-fields/custom-fields.interface';
import {
  CUSTOM_FIELDS_STATUS,
  CUSTOM_FIELDS_TYPES,
  DEFAULT_CUSTOM_FIELD,
} from 'features/custom-fields/custom-fields.constants';
import { TemplateCustomFieldsService } from '../template/services/template-custom-fields.service';
import { Observable, Subject } from 'rxjs';
import { getErrorMessage } from 'utils/helpers';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { required } from '@certemy/common';
import { CUSTOM_FIELD_TYPES, DEFAULT_MODAL_OPTIONS } from 'constants/constants';
import { takeUntil } from 'rxjs/operators';
import { CUSTOM_MODAL_VIEW_MODE, CustomFieldCreationParams } from '../custom-fields.interface';
import _sortBy from 'lodash-es/sortBy';
@Component({
  selector: 'cm-custom-field-modal',
  templateUrl: './custom-field-modal.component.html',
  styleUrls: ['./custom-field-modal.component.scss'],
})
export class CustomFieldModalComponent implements OnInit, OnDestroy {
  @ViewChild('customFieldTemplate') customFieldTemplate;
  fieldForm: UntypedFormGroup;
  custGroups: CustomFields.Group[] = [];
  validationErrors: string[] = [];
  showError = false;
  onClose = new Subject<null | CustomFields.Item[]>();
  CUSTOM_FIELDS_TYPES_LIST = [];
  currentViewType = CUSTOM_MODAL_VIEW_MODE.FORM;
  onDestroy$ = new Subject<void>();
  isMapped = false;
  isEncrypted = false;
  isEncryptingAllowed = true;
  encryptTooltip: string;
  secureWords: string[] = [];
  readonly CUSTOM_FIELDS_TYPES = CUSTOM_FIELDS_TYPES;
  readonly VIEW_MODE = CUSTOM_MODAL_VIEW_MODE;
  readonly CUSTOM_FIELDS_STATUS_LIST = Object.values(CUSTOM_FIELDS_STATUS);
  readonly CUSTOM_FIELD_TYPES = CUSTOM_FIELD_TYPES;
  readonly DISABLED_FIELDS = ['name', 'label', 'status_id', 'type_id'];
  private createForOrganization: number;

  constructor(
    private modalService: NgbModal,
    private templateCustomFieldsService: TemplateCustomFieldsService,
    private fb: UntypedFormBuilder,
  ) {}

  ngOnInit() {
    const { TEXTAREA, ...restCustomFields } = CUSTOM_FIELDS_TYPES;
    this.CUSTOM_FIELDS_TYPES_LIST = _sortBy([TEXTAREA, ...Object.values(restCustomFields)], 'name');

    this.templateCustomFieldsService
      .getCustomSecureWords()
      .subscribe((words: string[]) => (this.secureWords = words.map((word) => word.toLocaleLowerCase())));

    this.encryptTooltip =
      'If this information needs to be encrypted, please check this box. For example, a Social Security Number field should be encrypted. However, standard fields such as “First Name” and “Last Name” do NOT need to be encrypted.  Please note any field that is encrypted will NOT be available for display in the public registry or any reports.';
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  close() {
    this.onClose.next(null);
  }

  private validateSecureWordsAndSave() {
    const { name, label, is_encrypted, type_id } = this.fieldForm.getRawValue();
    const shouldBeChecked = !is_encrypted && type_id !== CUSTOM_FIELD_TYPES.ATTACHMENT;
    if (!shouldBeChecked) {
      this.submit();
    } else {
      const isPotentiallySecure = this.secureWords.some(
        (word) => name.toLowerCase().includes(word) || label.toLowerCase().includes(word),
      );
      isPotentiallySecure ? (this.currentViewType = CUSTOM_MODAL_VIEW_MODE.ENCRYPTED_INFO) : this.submit();
    }
  }

  save() {
    if (this.fieldForm.valid) {
      this.showError = false;
      this.validationErrors = [];
      this.validateSecureWordsAndSave();
    } else {
      this.showError = true;
      this.validationErrors = ['Please fill in all required fields'];
    }
  }

  encryptAndSubmit() {
    this.fieldForm.patchValue({ is_encrypted: true });
    this.submit();
  }

  submit() {
    this.templateCustomFieldsService
      .createOrUpdateCustomField(this.fieldForm.getRawValue(), this.createForOrganization)
      .subscribe(
        (res) => this.onClose.next(res),
        (err) => {
          this.currentViewType = CUSTOM_MODAL_VIEW_MODE.FORM;
          const defaultError = this.fieldForm.value.field_id
            ? 'An error occurred while updating field.'
            : 'An error occurred while creating field.';
          this.validationErrors = [getErrorMessage(err, defaultError)];
        },
        () => (this.currentViewType = CUSTOM_MODAL_VIEW_MODE.FORM),
      );
  }

  open({ custField, custGroups = [], createForOrganization }: CustomFieldCreationParams) {
    this.initForm(custField);
    this.custGroups = custGroups;
    this.createForOrganization = createForOrganization;
    this.subscribeOnFieldsChange();

    return Observable.create((observer) => {
      const modalRef = this.openModal();
      const onCloseSub = this.onClose.subscribe(observer);
      return () => {
        modalRef.close();
        onCloseSub.unsubscribe();
      };
    });
  }

  subscribeOnFieldsChange() {
    const { status_id: statusControl, type_id: typeControl } = this.fieldForm.controls;

    typeControl.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((type: number) => {
      this.updateEncrypted(type);
      this.updateDefaultValue('');
    });
    statusControl.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((newStatus) => this.onFieldStatusChange(newStatus));
  }

  updateEncrypted(type: number) {
    const { disabled, value } = this.fieldForm.controls.is_encrypted;
    this.isEncrypted = disabled ? this.isEncrypted : value;

    if (type === CUSTOM_FIELD_TYPES.ATTACHMENT) {
      const { is_encrypted } = this.fieldForm.getRawValue();
      this.isEncrypted = is_encrypted;
      this.fieldForm.patchValue({ is_encrypted: false });
      this.fieldForm.controls.is_encrypted.disable();
    } else {
      this.fieldForm.patchValue({ is_encrypted: this.isEncrypted });
      this.isEncryptingAllowed
        ? this.fieldForm.controls.is_encrypted.enable()
        : this.fieldForm.controls.is_encrypted.disable();
    }
  }

  updateDefaultValue(newValue) {
    this.fieldForm.patchValue({ default_value: newValue });
  }

  isFieldStatusInactive(status) {
    return status === CUSTOM_FIELDS_STATUS.INACTIVE.id;
  }

  onFieldStatusChange(status) {
    if (this.isFieldStatusInactive(status)) {
      this.fieldForm.patchValue({ use_in_profile: false, use_in_list: false });
      this.fieldForm.controls.use_in_profile.disable();
      this.fieldForm.controls.use_in_list.disable();
    } else {
      this.fieldForm.controls.use_in_profile.enable();
      this.fieldForm.controls.use_in_list.enable();
    }
  }

  private initForm(customField) {
    customField = { ...DEFAULT_CUSTOM_FIELD, ...customField };
    const {
      group_id,
      type_id,
      field_id,
      is_required,
      is_encrypted,
      professional_read_only,
      use_in_list,
      default_value,
      status_id,
      use_in_profile,
      is_mapped,
      label,
      name,
      is_encrypting_enabled,
    } = customField;
    this.isMapped = is_mapped;
    this.isEncrypted = is_encrypted;
    this.isEncryptingAllowed = type_id !== CUSTOM_FIELD_TYPES.ATTACHMENT ? is_encrypting_enabled : true;
    this.showError = false;
    this.validationErrors = [];
    this.fieldForm = this.fb.group({
      name: this.fb.control(name, [required]),
      label: this.fb.control(label, [required]),
      group_id,
      type_id,
      field_id,
      is_required,
      is_encrypted: this.fb.control({ value: is_encrypted, disabled: !is_encrypting_enabled }),
      professional_read_only,
      use_in_list,
      default_value,
      status_id,
      use_in_profile,
    });

    if (this.isFieldStatusInactive(status_id)) {
      this.fieldForm.controls.use_in_profile.disable();
      this.fieldForm.controls.use_in_list.disable();
    }

    if (this.isMapped) {
      this.DISABLED_FIELDS.forEach((field) => this.fieldForm.controls[field].disable());
    }
  }

  private openModal() {
    const modalRef = this.modalService.open(NgbCustomModalComponent, {
      ...DEFAULT_MODAL_OPTIONS,
      windowClass: 'x-lg',
    });
    const title = this.fieldForm.value.field_id ? 'Edit custom field' : 'Add new custom field';
    modalRef.componentInstance.template = this.customFieldTemplate;
    const modalConfig: CustomModalConfig = {
      title: '',
      isCloseCrossVisible: false,
      hasCustomFooter: true,
      type: 'organization',
      hideHeader: false,
    };
    modalRef.componentInstance.config = { ...modalConfig, title };
    return modalRef;
  }
}
